import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, StatCard, SectionCard, Empty } from "@/components/shared";
import { inr, mt, num } from "@/lib/format";
import { Users, FolderKanban, TrendingUp, Truck, Gift, Building2, Award, Activity } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
  FunnelChart, Funnel, LabelList, Cell as PieCell,
} from "recharts";

const COPPER = "#C86D23";
const NAVY = "#0F1E36";
const TIER_PIE = { Silver: "#94A3B8", Gold: "#D97706", Platinum: "#6366F1", Diamond: "#06B6D4" };

export default function CommandCentre() {
  const [k, setK] = useState(null);
  const [funnel, setFunnel] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [geo, setGeo] = useState([]);
  const [loyalty, setLoyalty] = useState(null);
  const [dist, setDist] = useState([]);
  const [comp, setComp] = useState([]);

  useEffect(() => {
    api.get("/command/kpis").then((r) => setK(r.data));
    api.get("/command/funnel").then((r) => setFunnel(r.data));
    api.get("/command/pipeline").then((r) => setPipeline(r.data));
    api.get("/command/geography").then((r) => setGeo(r.data));
    api.get("/loyalty/summary").then((r) => setLoyalty(r.data));
    api.get("/command/dashboards/distributor").then((r) => setDist(r.data.by_distributor || []));
    api.get("/command/dashboards/competitor").then((r) => setComp(r.data.by_competitor || []));
  }, []);

  const tierData = loyalty ? Object.entries(loyalty.tier_distribution).map(([name, value]) => ({ name, value })) : [];
  const maxGeoMt = Math.max(...geo.map((g) => g.mt), 1);

  return (
    <div>
      <PageHeader title="Command Centre" subtitle="Executive KPIs, commercial funnel and network intelligence — live from the database." testid="command-centre-kpi-grid" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6 stagger">
        <StatCard label="Verified Partners" value={k ? `${k.partners_verified}/${k.partners_total}` : "—"} sub={`${k?.partners_pending || 0} pending`} accent={COPPER} icon={Users} testid="kpi-partners" />
        <StatCard label="Pipeline Value" value={k ? inr(k.pipeline_value) : "—"} sub={`${k ? mt(k.pipeline_mt) : ""} in play`} accent="#2563EB" icon={TrendingUp} testid="kpi-pipeline" />
        <StatCard label="Supplied Tonnage" value={k ? mt(k.supplied_mt) : "—"} sub={k ? inr(k.supplied_value) : ""} accent="#059669" icon={Truck} testid="kpi-supplied" />
        <StatCard label="Projects" value={k ? num(k.projects_total) : "—"} sub={`${k?.won_projects || 0} won`} accent="#7C3AED" icon={FolderKanban} testid="kpi-projects" />
        <StatCard label="Points Outstanding" value={k ? num(k.points_outstanding) : "—"} sub="redeemable" accent={COPPER} icon={Gift} testid="kpi-points-out" />
        <StatCard label="Points Pending" value={k ? num(k.points_pending) : "—"} sub="awaiting approval" accent="#D97706" icon={Award} testid="kpi-points-pending" />
        <StatCard label="Loyalty Members" value={loyalty ? num(loyalty.active_members) : "—"} sub="active earners" accent="#0891B2" icon={Activity} testid="kpi-members" />
        <StatCard label="Cities Covered" value={geo.length || 8} sub="pan-India network" accent={NAVY} icon={Building2} testid="kpi-cities" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <SectionCard title="Commercial MT Funnel" className="lg:col-span-2" testid="mt-funnel">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={funnel} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <XAxis dataKey="stage" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => `${v} MT`} contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
              <Bar dataKey="mt" radius={[6, 6, 0, 0]} fill={COPPER} />
            </BarChart>
          </ResponsiveContainer>
        </SectionCard>

        <SectionCard title="Tier Distribution" testid="tier-distribution">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={tierData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                {tierData.map((e) => <Cell key={e.name} fill={TIER_PIE[e.name] || "#CBD5E1"} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-3 justify-center">
            {tierData.map((t) => (
              <span key={t.name} className="flex items-center gap-1.5 text-xs text-slate-600">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: TIER_PIE[t.name] }} />{t.name} ({t.value})
              </span>
            ))}
          </div>
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <SectionCard title="₹ Pipeline by Stage" className="lg:col-span-2" testid="pipeline-by-stage">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={pipeline} layout="vertical" margin={{ left: 20 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="stage" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} width={90} />
              <Tooltip formatter={(v) => inr(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
              <Bar dataKey="value" radius={[0, 6, 6, 0]} fill={NAVY} />
            </BarChart>
          </ResponsiveContainer>
        </SectionCard>

        <SectionCard title="Competitor Losses (MT)" testid="competitor-losses">
          {comp.length === 0 ? <Empty /> : (
            <div className="space-y-2.5">
              {comp.map((c) => (
                <div key={c.name} className="flex items-center justify-between text-sm">
                  <span className="text-slate-700">{c.name}</span>
                  <span className="font-mono font-semibold text-red-600">{c.mt} MT</span>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <SectionCard title="Geography Heat-map" className="lg:col-span-2" testid="geography-heatmap">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {geo.map((g) => {
              const intensity = g.mt / maxGeoMt;
              return (
                <div key={g.city} className="rounded-lg p-3 border" style={{
                  background: `rgba(200,109,35,${0.08 + intensity * 0.5})`,
                  borderColor: "rgba(200,109,35,0.25)",
                }} data-testid={`geo-${g.city}`}>
                  <p className="text-xs font-semibold text-slate-800">{g.city}</p>
                  <p className="font-mono text-lg font-extrabold text-slate-900">{g.mt}<span className="text-xs font-normal text-slate-500"> MT</span></p>
                  <p className="text-[11px] text-slate-500">{g.projects} projects · {g.partners} partners</p>
                </div>
              );
            })}
          </div>
        </SectionCard>

        <SectionCard title="Top Distributors (MT)" testid="top-distributors">
          {dist.length === 0 ? <Empty /> : (
            <div className="space-y-2.5">
              {dist.slice(0, 8).map((d) => (
                <div key={d.name} className="flex items-center justify-between text-sm">
                  <span className="text-slate-700 truncate mr-2">{d.name}</span>
                  <span className="font-mono font-semibold text-slate-900">{d.mt}</span>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
